// The side-effect entry the runtime loads: fetch the manifest, create the
// collab, paint the default UI. Everything it does, a consumer of
// `@spacefast/collab` can do by hand — this file exists so a served space does
// not have to.
//
// `window.Spacefast.collab` is published BEFORE the manifest arrives, because a
// page's own script may call `open()` on first paint and must not have to know
// how far along this boot is. Calls made before the collab exists are queued,
// and only the last lifecycle intent is replayed: open→close must never start a
// connection that only close itself could abort.

import { COLLAB_MANIFEST_PATH, collabThemeUrl, fetchCollabManifest } from "./config.js";
import { createCollab, type Collab, type CollabOptions } from "./core.js";
import { collabEnabled } from "./manifest.js";
import { reportCollabError } from "./report.js";
import { createShell, type Shell } from "./shell/element.js";
import { BOOT_ORB_ID, type CollabIdentity } from "./store/types.js";
import { adoptStyles } from "./theme/adopt.js";
import {
  accountRenewalOf,
  settleAccountRenewal,
  startAccountRenewal,
} from "./transport/account-renewal.js";

type PendingCall = {
  method: "open" | "close";
  resolve: () => void;
  reject: (cause: unknown) => void;
};

export type CollabPublicApi = {
  open(): Promise<void>;
  close(): Promise<void>;
  readonly identity: CollabIdentity | null;
};

type Bindable = Pick<Collab, "open" | "close"> & { readonly identity: CollabIdentity | null };

export function installStub(pending: PendingCall[]) {
  let runtime: Bindable | null = null;
  const enqueue = (method: PendingCall["method"]): Promise<void> => {
    if (runtime) return runtime[method]();
    return new Promise((resolve, reject) => pending.push({ method, resolve, reject }));
  };
  const api: CollabPublicApi = {
    open: () => enqueue("open"),
    close: () => enqueue("close"),
    get identity() {
      return runtime?.identity ?? null;
    },
  };

  return {
    api,
    async bind(collab: Bindable) {
      runtime = collab;
      const calls = pending.splice(0);
      const intent = calls.at(-1)?.method;
      if (!intent) return;
      let trailingStart = calls.length - 1;
      while (trailingStart > 0 && calls[trailingStart - 1]?.method === intent) {
        trailingStart -= 1;
      }
      for (const superseded of calls.slice(0, trailingStart)) superseded.resolve();
      const current = calls.slice(trailingStart);
      try {
        await collab[intent]();
        for (const call of current) call.resolve();
      } catch (error) {
        for (const call of current) call.reject(error);
      }
    },
  };
}

const headless: Bindable = {
  identity: null,
  open: async () => undefined,
  close: async () => undefined,
};

/** The bootstrap's placeholder orb has nothing left to stand in for. */
function dismissBootOrb(): void {
  globalThis.document?.getElementById(BOOT_ORB_ID)?.remove();
}

/**
 * Where the manifest is, as the injected script declares it. The runtime writes
 * `data-sf-config` so a preview session's token rides the same URL; an
 * un-annotated page falls back to the well-known path.
 *
 * Found by attribute, never by `document.currentScript`: the runtime injects
 * this bundle with `type="module"`, and `currentScript` is null for the whole
 * life of a module. Reading it lost the preview token on every preview session.
 * The last match wins — a second tag is a page that was served twice, and the
 * later one is the one whose config this run should honour.
 */
export function manifestUrl(): string {
  const tags = globalThis.document?.querySelectorAll<HTMLElement>("script[data-sf-config]");
  const declared = tags?.length ? tags[tags.length - 1]?.dataset["sfConfig"] : null;
  return declared || COLLAB_MANIFEST_PATH;
}

/**
 * The space's own theme, adopted INSIDE the shadow root.
 *
 * `sdk.js` links `collab.css` into the document, and that link is what a
 * `ui: "custom"` build reads — but a document stylesheet cannot reach this
 * shadow root. Its `:host{}` block matches nothing out there, and the bundle's
 * own `:host` fallbacks (the system font stack, the fallback accent) beat
 * anything that merely inherits in. So the default UI fetches the same bytes
 * and adopts them here, where `:host` means this overlay. Adopted after the
 * base sheet, so the space's values win on equal specificity.
 *
 * A stylesheet that will not load is cosmetic, never fatal: the bundle's own
 * tokens already paint a complete overlay.
 */
async function adoptSpaceTheme(root: ShadowRoot, url: string): Promise<void> {
  try {
    const target = new URL(url, globalThis.location?.href ?? "http://localhost/");
    const response = await fetch(target, { credentials: "same-origin" });
    if (!response.ok) return;
    adoptStyles(root, await response.text());
  } catch {
    // Themed or not, the overlay still runs.
  }
}

/** The same collab, whose `destroy()` also takes the painted layer down. */
function withCleanup(core: Collab, destroy: () => void): Collab {
  return {
    store: core.store,
    manifest: core.manifest,
    theme: core.theme,
    get identity() {
      return core.identity;
    },
    get host() {
      return core.host;
    },
    getToken: () => core.getToken(),
    getTelemetryTicket: () => core.getTelemetryTicket(),
    getLiveTicket: () => core.getLiveTicket(),
    onTicketsChange: (cb) => core.onTicketsChange(cb),
    sendEphemeral: (event, payload) => core.sendEphemeral(event, payload),
    open: () => core.open(),
    close: () => core.close(),
    destroy,
  };
}

export async function mount(configUrl: string = manifestUrl()): Promise<Collab | null> {
  const spacefast = (globalThis.Spacefast ??= {});
  const pending: PendingCall[] = [];
  const stub = installStub(pending);
  spacefast.collab = stub.api;

  let collab: Collab | null = null;
  let shell: Shell | null = null;
  let overlay: { destroy(): void } | null = null;
  let stopped = false;
  const destroy = () => {
    stopped = true;
    overlay?.destroy();
    overlay = null;
    collab?.destroy();
    shell?.destroy();
    dismissBootOrb();
  };
  try {
    const manifest = await fetchCollabManifest(configUrl);
    // This entry is the default UI's. A space that brings its own builds it on
    // `createCollab` — painting ours over theirs is the one thing `ui: "custom"`
    // is asking us not to do.
    if (!collabEnabled(manifest) || manifest.ui !== "default") {
      dismissBootOrb();
      await stub.bind(headless);
      return null;
    }
    // The shell owns the host element, its shadow root and the token sheet; the
    // core mounts its interaction shields into the same host, so the picker and
    // the React tree are walled off from the customer page together.
    shell = globalThis.document ? createShell() : null;
    if (shell) await adoptSpaceTheme(shell.root, collabThemeUrl(configUrl));
    // A renewal that came back without an account leaves the page as it was.
    if (shell) settleAccountRenewal();
    const options: CollabOptions = {
      manifest,
      onRefusal: () => {
        // A refused ticket is not an outage: Comments are unavailable to this
        // visitor, so the layer goes away entirely — no orb, no error chrome,
        // no reconnect loop it could never finish.
        destroy();
        void stub.bind(headless);
      },
    };
    if (shell) {
      options.host = shell.host;
      // An editor whose session here lapsed signs back in, rather than reading
      // their own Space as a visitor.
      const renewal = accountRenewalOf(manifest);
      options.renewAccount = () => startAccountRenewal(renewal);
    }
    collab = createCollab(options);
    await stub.bind(collab);
    if (!shell) return collab;
    const { mountDefaultUi } = await import("./react/mount.js");
    if (stopped) return null;
    overlay = mountDefaultUi(collab, shell.root);
    // Whoever destroys this collab destroys everything this boot made: the
    // painted layer included. Leaving the React tree behind leaves its window
    // listeners behind with it.
    return withCleanup(collab, destroy);
  } catch (error) {
    destroy();
    const failure = reportCollabError("boot", error);
    await stub.bind({
      identity: null,
      open: async () => Promise.reject(failure),
      close: async () => undefined,
    });
    return null;
  }
}

void mount();
